import type { QueryClient } from '@tanstack/react-query';
import type { getConfigByKey } from '@tn/configs';
import { appLogger } from '@tn/foundation/utils/app-logger/appLogger';
import { AUTH_COOKIE_KEY } from '@tn/foundation/utils/constants/common';
import { runPlatformRestart } from '@tn/foundation/utils/platformRestart';
import { getCookie } from 'cookies-next';

import { refreshFullUserProfile } from './helpers';

type PostAuthFlow = 'signIn' | 'signUp';

type RunPostAuthClientSetupParams = {
    queryClient: QueryClient;
    getConfigByKey: ReturnType<typeof getConfigByKey>;
    flow: PostAuthFlow;
    skipProfileRefresh?: boolean;
};

const FLOW_CONFIG = {
    signIn: {
        component: 'runPostSignInClientSetup',
        succeededLabel: 'Sign-in succeeded',
    },
    signUp: {
        component: 'runPostSignUpClientSetup',
        succeededLabel: 'Sign-up succeeded',
    },
} as const;

function hasAuthCookie(): boolean {
    return Boolean(getCookie(AUTH_COOKIE_KEY));
}

/**
 * Shared post-auth client steps after sign-in or sign-up succeeds and CBS_COM is set.
 * Failures are non-fatal when the auth cookie is present so the flow can navigate away
 * from the auth page (restart/profile hiccups must not block finish UX).
 *
 * Resolves to whether the full user profile was refreshed; when false the cache still holds
 * the pre-auth user.
 */
export async function runPostAuthClientSetup({
    queryClient,
    getConfigByKey,
    flow,
    skipProfileRefresh = false,
}: RunPostAuthClientSetupParams): Promise<boolean> {
    const { component, succeededLabel } = FLOW_CONFIG[flow];
    const platformRestartEnabled = getConfigByKey<boolean>('options.platformRestartEnabled');

    if (platformRestartEnabled) {
        try {
            // Authoritative so the signed-in/up JWT wins over a concurrent anonymous Start; the
            // identity marks the session so a later fresh Start can't downgrade it.
            await runPlatformRestart([], undefined, {
                authoritative: true,
                identity: 'authenticated',
            });
        } catch (error) {
            if (!hasAuthCookie()) {
                throw error;
            }
            appLogger.warn(`${succeededLabel} but platformRestart failed; continuing`, {
                component,
                step: 'platformRestart',
            });
        }
    }

    if (skipProfileRefresh) {
        return false;
    }
    try {
        await refreshFullUserProfile(queryClient);
    } catch (error) {
        if (!hasAuthCookie()) {
            throw error;
        }
        appLogger.warn(`${succeededLabel} but profile refresh failed; continuing`, {
            component,
            step: 'profileRefresh',
        });
        return false;
    }
    return true;
}
